import { Component, Inject, OnDestroy } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { MatAutocompleteTrigger } from "@angular/material/autocomplete";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { TranslateService } from "@ngx-translate/core";
import { map, Observable, Subject } from "rxjs";
import LocalizationConstants from "src/app/constants/localization-constants";
import { Company } from "src/app/models/Company";
import CustomerSettings from "src/app/models/CustomerSettings";
import { UnitOfMeasure } from "src/app/models/Enums";
import { SelectOption } from "src/app/models/SelectOption";
import { ShippingAccount } from "src/app/models/ShippingAccount";
import { CompanyService } from "src/app/services/company.service";
import CustomerSettingsService from "src/app/services/customer-settings.service";
import { GlobalOverlayService } from "src/app/services/global-overlay.service";
import { ModalComponent } from "src/app/services/modal.service";

export type CustomerSettingsEditModalComponentData = {
  editCompanySettings?: CustomerSettings;
  companySettingsList: CustomerSettings[];
  availableShippingAccounts: ShippingAccount[];
};

@Component({
  selector: "app-customer-settings-edit-modal",
  templateUrl: "./customer-settings-edit-modal.component.html",
  styleUrls: ["./customer-settings-edit-modal.component.scss"],
  standalone: false,
})
export class CustomerSettingsEditModalComponent
  implements
    OnDestroy,
    ModalComponent<
      CustomerSettingsEditModalComponent,
      CustomerSettingsEditModalComponentData,
      CustomerSettings
    >
{
  readonly localizationConstants = LocalizationConstants;
  private readonly _destroying$ = new Subject<void>();
  editCompanySettings?: CustomerSettings;
  isEditMode = false;
  loadedCompanySettingsList: CustomerSettings[] = [];

  companiesToAdd$: Observable<Company[]> = this.companyService.companies$.pipe(
    map((x) =>
      x.filter((c) => !this.loadedCompanySettingsList.some((z) => z.companyId === c.name)),
    ),
  );

  unitsOfMeasure: SelectOption[] = [
    new SelectOption(
      this.translateService.instant(this.localizationConstants.UNIT_OF_MEASURE_METRIC),
      UnitOfMeasure.Metric,
    ),
    new SelectOption(
      this.translateService.instant(this.localizationConstants.UNIT_OF_MEASURE_IMPERIAL),
      UnitOfMeasure.Imperial,
    ),
  ];

  faSpinner = faSpinner;

  form: FormGroup;
  companyIdControl = new FormControl(null, [
    Validators.required,
    Validators.maxLength(20),
    Validators.minLength(5),
  ]);
  companyNameControl = new FormControl(null);
  shippingCompanyControl = new FormControl(null, [Validators.maxLength(200)]);
  shippingAccountControl = new FormControl(null, [Validators.maxLength(200)]);
  unitOfMeasureControl = new FormControl(null, [Validators.maxLength(200)]);
  shippingNotesControl = new FormControl(null, [Validators.maxLength(500)]);
  isShippingEnabledControl = new FormControl(false);
  isTechCoverEnabledControl = new FormControl(false);

  constructor(
    public dialogRef: MatDialogRef<CustomerSettingsEditModalComponent, CustomerSettings>,
    private translateService: TranslateService,
    public companyService: CompanyService,
    private overlayService: GlobalOverlayService,
    private customerSettingsService: CustomerSettingsService,
    @Inject(MAT_DIALOG_DATA)
    public data: CustomerSettingsEditModalComponentData,
  ) {
    this.dialogRef.disableClose = true;
    this.isEditMode = !!data.editCompanySettings;
    this.editCompanySettings = data.editCompanySettings;
    this.loadedCompanySettingsList = data.companySettingsList;
    this.setForm();
  }

  public async closeDialogAsync(shouldAddSettings: boolean): Promise<void> {
    if (!shouldAddSettings) {
      this.dialogRef.close(null);
      return;
    }

    if (!this.validateForm()) return;

    this.overlayService.show();

    try {
      if (this.isEditMode) {
        await this.customerSettingsService.updateAsync(this.form.value);
      } else {
        await this.customerSettingsService.addAsync(this.form.value);
      }
    } finally {
      this.overlayService.close();
    }

    this.dialogRef.close(new CustomerSettings().deserialize(this.form.value));
  }

  private setForm(): void {
    this.form = new FormGroup({
      companyId: this.companyIdControl,
      shippingAccountId: this.shippingAccountControl,
      unitOfMeasure: this.unitOfMeasureControl,
      shippingNotes: this.shippingNotesControl,
      isShippingEnabled: this.isShippingEnabledControl,
      isTechCoverEnabled: this.isTechCoverEnabledControl,
      companyName: this.companyNameControl,
    });
    if (!this.editCompanySettings) return;
    this.form.patchValue(this.editCompanySettings);
  }

  private validateForm(): boolean {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return false;
    }

    return true;
  }

  onCompanySelectInput(event: Event): void {
    this.companyService.companyInput$.next((event.target as HTMLInputElement).value);
  }

  companyAutocompleteDisplayFn(company: Company): string {
    return company ? company.fullName : "";
  }

  onCompanySelectionChange(company: Company): void {
    this.companyIdControl.setValue(company.name);
    this.companyNameControl.setValue(company.fullName);
  }

  onShippingEnabledChange(value: boolean): void {
    this.isShippingEnabledControl.setValue(value);
  }

  onTechCoverEnabledChange(value: boolean): void {
    this.isTechCoverEnabledControl.setValue(value);
  }

  openPanel(trigger: MatAutocompleteTrigger): void {
    !trigger.panelOpen && trigger.openPanel();
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
